import React, {useMemo, useState} from "react";
import {Alert, Container, Spinner} from "react-bootstrap";
import SalesFilterHeader from "../../Components/Statistics/SalesFilterHeader.jsx";
import useSalesFilters from "../../Components/Statistics/useSalesFilters.js";
import useStatisticsData from "../../Components/Statistics/useStatisticsData.js";
import SortimentTile from "../../Components/Statistics/SortimentTile.jsx";
import SortimentDetail from "../../Components/Statistics/SortimentDetail.jsx";
import {buildGroupColorMap, colorForGroup} from "../../Components/Statistics/format.js";

const LIMIT = 12;

/**
 * Sortimente - Umsatzverlauf je Übernummer als Grafiken, seit August 2026
 * eine eigene Unterseite (siehe StatisticsController::sortimente()). Neu
 * gegenüber der alten Dashboard-Ansicht: statt einer reinen Artikeltabelle
 * ein Raster kleiner Verlaufsdiagramme, ein Klick auf eine Kachel öffnet die
 * Detailansicht mit großem Diagramm und Variantentabelle.
 *
 * Die Auswertungsebene der Filterleiste (Artikel/Übernummer) wirkt sich auf
 * diese Seite bewusst NICHT aus - /statistics/sortiment-verlauf rechnet
 * immer auf Übernummer-Ebene (siehe SalesStatisticsQuery::sortimentVerlauf()),
 * das ist der ganze Zweck dieser Seite. Der Umschalter bleibt trotzdem
 * sichtbar, damit die Filterleiste auf allen Unterseiten gleich aussieht.
 */
export default function StatisticsSortimente() {
    const salesFilters = useSalesFilters();
    const {filters} = salesFilters;

    const [metric, setMetric] = useState("net_amount");
    const [granularity, setGranularity] = useState("month");
    const [selectedParent, setSelectedParent] = useState(null);

    const params = useMemo(() => ({...filters, limit: LIMIT, granularity}), [filters, granularity]);
    const {data, loading, error} = useStatisticsData("/statistics/sortiment-verlauf", params);
    const rows = data?.rows || [];

    const colorMap = useMemo(() => buildGroupColorMap(rows.map((row) => row.parent_article_number)), [rows]);

    const selectedRow = rows.find((row) => row.parent_article_number === selectedParent) || null;
    const selectedColor = selectedRow ? colorForGroup(colorMap, selectedRow.parent_article_number) : "#2f6fd0";

    return (
        <Container fluid className="pt-2 pb-4">
            <SalesFilterHeader title="Sortimente" salesFilters={salesFilters}>
                {selectedParent ? (
                    <SortimentDetail
                        parentArticleNumber={selectedParent}
                        filters={filters}
                        metric={metric}
                        granularity={granularity}
                        color={selectedColor}
                        onBack={() => setSelectedParent(null)}
                    />
                ) : (
                    <div className="d-flex flex-column gap-3">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                            <div className="btn-group btn-group-sm" role="group" aria-label="Kennzahl">
                                <button
                                    type="button"
                                    aria-pressed={metric === "net_amount"}
                                    className={`btn ${metric === "net_amount" ? "btn-primary" : "btn-outline-secondary"}`}
                                    onClick={() => setMetric("net_amount")}
                                >
                                    Umsatz
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={metric === "quantity"}
                                    className={`btn ${metric === "quantity" ? "btn-primary" : "btn-outline-secondary"}`}
                                    onClick={() => setMetric("quantity")}
                                >
                                    Menge
                                </button>
                            </div>
                            <div className="btn-group btn-group-sm" role="group" aria-label="Granularität">
                                <button
                                    type="button"
                                    aria-pressed={granularity === "month"}
                                    className={`btn ${granularity === "month" ? "btn-primary" : "btn-outline-secondary"}`}
                                    onClick={() => setGranularity("month")}
                                >
                                    Monat
                                </button>
                                <button
                                    type="button"
                                    aria-pressed={granularity === "year"}
                                    className={`btn ${granularity === "year" ? "btn-primary" : "btn-outline-secondary"}`}
                                    onClick={() => setGranularity("year")}
                                >
                                    Jahr
                                </button>
                            </div>
                        </div>

                        {error && <Alert variant="danger">{error}</Alert>}

                        {loading && !data ? (
                            <div className="d-flex justify-content-center py-5">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Wird geladen…</span>
                                </Spinner>
                            </div>
                        ) : rows.length === 0 ? (
                            <div className="text-muted small text-center py-5">Keine Sortimente für den gewählten Zeitraum und Filter gefunden.</div>
                        ) : (
                            <div className="row g-3">
                                {rows.map((row) => (
                                    <div key={row.parent_article_number} className="col-sm-6 col-lg-4 col-xl-3">
                                        <SortimentTile
                                            row={row}
                                            color={colorForGroup(colorMap, row.parent_article_number)}
                                            metric={metric}
                                            onSelect={setSelectedParent}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </SalesFilterHeader>
        </Container>
    );
}
